<script lang="ts" setup>
import { getSplashesColumns } from '@/columns/splashesColumns';
import { getTickersColumns } from '@/columns/tickersColumns';
import AlertsFilters from '@/components/alerts/AlertsFilters.vue';
import ScrollPanel from 'primevue/scrollpanel'
import AlertsTable from '@/components/alerts/AlertsTable.vue';
import SrGroupDataTable from '@/components/tables/SrGroupDataTable.vue';
import { useSplashesCellFormatter } from '@/composable/formatters/useSplashesCellFormatter';
import { useSplashFilters } from '@/composable/filters/useSplashFilters';
import { useTickersCellFormatter } from '@/composable/formatters/useTickersCellFormatter';
import { useTickersFilters } from '@/composable/filters/useTickersFilters';
import { useSplashesStore } from '@/stores/splashesStore';
import { useTickersStore } from '@/stores/tickersStore';
import { keysToSnake, parseCommaSeparatedAssets } from '@/utils';
import type { FormSubmitEvent } from '@primevue/forms';
import { Card, Divider } from 'primevue';
import { computed, onMounted, ref, useTemplateRef } from 'vue';

const tickersCellFormatter = useTickersCellFormatter()

const tickersFormattingRules = computed(() => tickersCellFormatter.formattingRules.value)

const tickersTableColumns = getTickersColumns()
const tickersStore = useTickersStore()

const { formattingRules } = useSplashesCellFormatter()
const tableColumns = getSplashesColumns()
const splashesStore = useSplashesStore()

const alertsTable = useTemplateRef("alertsTable")
const alertsTable2 = useTemplateRef("alertsTable2")

const pageInited = ref(false)
const { filters, formData, zodForm } = useSplashFilters()

const usedtickersFilters = useTickersFilters()
const tickersFilters = computed(() => usedtickersFilters.filters)
const tickersFormData = computed(() => usedtickersFilters.formData.value)
const tickersZodForm = computed(() => usedtickersFilters.zodForm)

// Обработка формы фильтрации сплешей
const onFilterFormSubmit = async (e: FormSubmitEvent) => {
  if (!e.valid || !pageInited.value) {
    return
  }
  const values = keysToSnake(e.values)
  if (typeof values.symbols === 'string') {
    values.symbols = parseCommaSeparatedAssets(values.symbols)
  }
  splashesStore.filters = {
    ...splashesStore.filters,
    ...values
  }
  await alertsTable.value?.onPage(null)
}

// Обработка формы фильтрации тикеров
const onFilterFormSubmit2 = async (e: FormSubmitEvent) => {
  if (!e.valid || !pageInited.value) {
    return
  }

  tickersStore.filters = {
    ...tickersStore.filters,
    ...keysToSnake(e.values)
  }
  await alertsTable2.value?.onPage(null)
}

onMounted(async () => {
  tickersStore.clear()
  splashesStore.clear()
  await Promise.allSettled([
    alertsTable.value?.onPage(null, 'total_2h', -1),
    alertsTable2.value?.onPage(null, 'change', -1)
  ])
  pageInited.value = true
})
</script>

<template>
  <div class="grid gap-3 grid-cols-1 lg:grid-cols-2">
    <div>
      <Card pt:body:class="p-0" pt:root:class="rounded-none md:rounded-xl">
        <template #content>
          <AlertsFilters :inited="pageInited" name="splashes-filters" panel-class="px-2 border-none mb-0"
            :loading="splashesStore.loading" :form-submit="onFilterFormSubmit" :form-data="formData" :filters="filters"
            :zod="zodForm" grid-classes="grid-cols-1 gap-3 md:grid-cols-6">
            <template #top-left>
              <span class="pit success">{{ $t('splashesStats') }}</span>
            </template>
          </AlertsFilters>
          <Divider class="mt-0 mb-0"></Divider>
          <ScrollPanel>
            <div style="max-height: 600px;">
              <AlertsTable :paginator="true" :lazy="true" :formatting-rules="formattingRules" ref="alertsTable"
                :columns="tableColumns" :fake-rows-count="15" :store="splashesStore" :col-full="true"
                :groupSortField="splashesStore.sortBy" :groupSortOrder="splashesStore.sortOrder"
                :table-component="SrGroupDataTable" :table-component-props="{
                  groupBy: 'symbol'
                }">
              </AlertsTable>
            </div>
          </ScrollPanel>
        </template>
      </Card>
    </div>
    <div>
      <Card pt:body:class="p-0" pt:root:class="rounded-none md:rounded-xl">
        <template #content>
          <AlertsFilters :inited="pageInited" name="tickers-filters" panel-class="px-2 border-none mb-0"
            :loading="tickersStore.loading" :form-submit="onFilterFormSubmit2" :form-data="tickersFormData"
            :filters="tickersFilters" :zod="tickersZodForm" grid-classes="grid-cols-1 gap-3 md:grid-cols-2">
            <template #top-left>
              <span class="pit success">{{ $t('tickers24Label') }}</span>
            </template>
          </AlertsFilters>
          <Divider class="mt-0 mb-0"></Divider>
          <ScrollPanel>
            <div style="max-height: 600px;">
              <AlertsTable :paginator="true" :lazy="true" :formatting-rules="tickersFormattingRules" ref="alertsTable2"
                :columns="tickersTableColumns" :fake-rows-count="15" :store="tickersStore" :col-full="true"
                :groupSortField="tickersStore.sortBy" :groupSortOrder="tickersStore.sortOrder"
                :table-component="SrGroupDataTable" :table-component-props="{
                  groupBy: 'symbol'
                }">
              </AlertsTable>
            </div>
          </ScrollPanel>
        </template>
      </Card>
    </div>
  </div>
</template>
